import type {
  AnalyticsConfig,
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsProvider,
  PageViewData,
  UserIdentity,
} from '@polybank/shared';
import type { UmamiTracker } from './umami-types';
import { logger } from '@/utils/logger';

type QueuedCall =
  | {
      type: 'event';
      name: string;
      data: Record<string, string | number | boolean>;
    }
  | { type: 'identify'; data: Record<string, string | number | boolean> };

function flattenData(
  obj: Record<string, unknown>
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = value;
    } else if (typeof value === 'object') {
      const nested = flattenData(value as Record<string, unknown>);
      for (const [nestedKey, nestedValue] of Object.entries(nested)) {
        result[`${key}_${nestedKey}`] = nestedValue;
      }
    }
  }
  return result;
}

export class UmamiAnalyticsProvider implements AnalyticsProvider {
  private config: AnalyticsConfig = { enabled: false };
  private queue: QueuedCall[] = [];
  private ready = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  init(config: AnalyticsConfig): void {
    this.config = config;

    if (!config.enabled) {
      this.debug('Analytics disabled');
      return;
    }

    this.waitForUmami();
  }

  trackPageView(_data?: PageViewData): void {
    // No-op: Umami auto-tracks page views via the script tag's History API patching
  }

  trackEvent<E extends AnalyticsEventName>(
    name: E,
    data: AnalyticsEventMap[E]
  ): void {
    if (!this.config.enabled) return;

    const flat = flattenData(data as Record<string, unknown>);
    this.debug(`trackEvent: ${name}`, flat);

    if (this.ready) {
      this.send('event', name, flat);
    } else {
      this.queue.push({ type: 'event', name, data: flat });
    }
  }

  identify(identity: UserIdentity): void {
    if (!this.config.enabled) return;

    const data = { userId: identity.anonymousId };
    this.debug('identify', data);

    if (this.ready) {
      this.send('identify', undefined, data);
    } else {
      this.queue.push({ type: 'identify', data });
    }
  }

  resetIdentity(): void {
    // No-op: Umami doesn't have a session reset API
  }

  // --- private ---

  private get umami(): UmamiTracker | undefined {
    return typeof window !== 'undefined' ? window.umami : undefined;
  }

  private waitForUmami(): void {
    if (this.umami) {
      this.onReady();
      return;
    }

    const maxWaitMs = 10_000;
    const intervalMs = 500;
    let elapsed = 0;

    this.pollTimer = setInterval(() => {
      elapsed += intervalMs;
      if (this.umami) {
        this.onReady();
      } else if (elapsed >= maxWaitMs) {
        this.debug('Umami script not loaded after 10s (ad blocker?)');
        this.cleanup();
      }
    }, intervalMs);
  }

  private onReady(): void {
    this.ready = true;
    this.cleanup();
    this.debug('Umami ready, flushing queue', { queued: this.queue.length });
    this.flushQueue();
  }

  private cleanup(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private flushQueue(): void {
    for (const call of this.queue) {
      if (call.type === 'event') {
        this.send('event', call.name, call.data);
      } else {
        this.send('identify', undefined, call.data);
      }
    }
    this.queue = [];
  }

  private send(
    type: 'event' | 'identify',
    name?: string,
    data?: Record<string, string | number | boolean>
  ): void {
    const u = this.umami;
    if (!u) return;

    if (type === 'event' && name) {
      u.track(name, data);
    } else if (type === 'identify' && data) {
      u.identify(data);
    }
  }

  private debug(message: string, data?: unknown): void {
    if (this.config.debug) {
      if (data !== undefined) {
        logger.debug(`[Analytics] ${message}`, data);
      } else {
        logger.debug(`[Analytics] ${message}`);
      }
    }
  }
}
